import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Search, UserPlus, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { labelNivel } from '@/lib/labels';

interface Preview {
  dni: string;
  candidato_destino: {
    region: string;
    provincia: string | null;
    distrito: string | null;
    cargo: string;
    orden: number | null;
    tipo_eleccion: string;
  };
  reniec: {
    encontrado: boolean;
    nombres: string | null;
    apellido_paterno: string | null;
    apellido_materno: string | null;
    sexo: string | null;
    fecha_nacimiento: string | null;
    edad_al_cierre: number | null;
    ubigeo_reniec: string | null;
    direccion: string | null;
  };
  padron_postulable: {
    encontrado: boolean;
    op_canonica: string | null;
    desafiliado: boolean | null;
    alianza_primarias: string | null;
  };
  duplicado_en_sistema: {
    ya_es_candidato: boolean;
    candidato_id: number | null;
  };
  bloqueantes: string[];
}

export function AsignarDesignadoForm({
  candidatoId,
  onSuccess,
}: {
  candidatoId: number;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [dni, setDni] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overrideBloqueantes, setOverrideBloqueantes] = useState(false);

  const previewMut = useMutation({
    mutationFn: async () => {
      setError(null);
      const { data } = await api.get<Preview>(
        `/candidatos/${candidatoId}/asignar-designado/preview`,
        { params: { dni } }
      );
      return data;
    },
    onSuccess: (data) => setPreview(data),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error consultando DNI'),
  });

  const asignarMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/candidatos/${candidatoId}/asignar-designado`,
        { dni, override_bloqueantes: overrideBloqueantes }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cand', String(candidatoId)] });
      qc.invalidateQueries({ queryKey: ['docs', candidatoId] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
      onSuccess();
    },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al asignar'),
  });

  const dniLimpio = dni.trim().replace(/\D/g, '');
  const dniValido = /^\d{8}$/.test(dniLimpio);

  return (
    <section className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 space-y-4">
      <header className="flex items-center gap-2 text-amber-900">
        <UserPlus size={20} />
        <div>
          <h3 className="text-sm font-bold uppercase">Asignar designado pendiente</h3>
          <p className="text-[11px] text-amber-800">
            Ingresa el DNI entregado por el CEN. El sistema verifica RENIEC,
            padrón postulable y crea el usuario del candidato.
          </p>
        </div>
      </header>

      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={8}
          value={dni}
          onChange={(e) => {
            setDni(e.target.value.replace(/\D/g, ''));
            setPreview(null);
            setError(null);
          }}
          placeholder="DNI 8 dígitos"
          className="flex-1 border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-arpp-primary"
        />
        <button
          onClick={() => previewMut.mutate()}
          disabled={!dniValido || previewMut.isPending}
          className="bg-arpp-primary text-white px-4 py-2 rounded text-sm hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2"
        >
          <Search size={14} /> {previewMut.isPending ? 'Consultando…' : 'Consultar DNI'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 flex items-center gap-2">
          <XCircle size={14} /> {error}
        </div>
      )}

      {preview && (
        <div className="bg-white border border-amber-200 rounded p-4 space-y-3">
          <header className="text-sm font-semibold text-arpp-primary">
            Preview de asignación · DNI {preview.dni}
          </header>

          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div className="space-y-1">
              <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
                {preview.reniec.encontrado ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                RENIEC (Factiliza)
              </div>
              <div>Nombres: <strong>{preview.reniec.nombres ?? '—'}</strong></div>
              <div>Apellidos: <strong>{preview.reniec.apellido_paterno ?? '—'} {preview.reniec.apellido_materno ?? ''}</strong></div>
              <div>Sexo: <strong>{preview.reniec.sexo ?? '—'}</strong></div>
              <div>Edad al cierre: <strong>{preview.reniec.edad_al_cierre ?? '—'}</strong></div>
              <div>Fnac: {preview.reniec.fecha_nacimiento ?? '—'}</div>
              <div>Ubigeo RENIEC: {preview.reniec.ubigeo_reniec ?? '—'}</div>
              <div className="text-gray-600 text-[11px]">{preview.reniec.direccion ?? '—'}</div>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
                {preview.padron_postulable.encontrado && !preview.padron_postulable.desafiliado
                  ? <CheckCircle2 size={14} className="text-green-600" />
                  : <XCircle size={14} className="text-red-600" />}
                Padrón postulable DNROP-JNE
              </div>
              <div>OP canónica: <strong>{preview.padron_postulable.op_canonica ?? '—'}</strong></div>
              <div>Alianza primarias: {preview.padron_postulable.alianza_primarias ?? '—'}</div>
              <div>Desafiliado: <strong className={preview.padron_postulable.desafiliado ? 'text-red-600' : ''}>{preview.padron_postulable.desafiliado ? 'SÍ ⚠' : 'No'}</strong></div>
              {preview.duplicado_en_sistema.ya_es_candidato && (
                <div className="text-red-600 mt-2">
                  ⚠ DNI ya asignado a candidato id={preview.duplicado_en_sistema.candidato_id}
                </div>
              )}
            </div>
          </div>

          <div className="text-xs border-t pt-2">
            <div className="font-semibold text-gray-700 mb-1">Posición destino</div>
            <div>
              {labelNivel(preview.candidato_destino.tipo_eleccion)} · {preview.candidato_destino.cargo}
              {preview.candidato_destino.orden ? ` pos ${preview.candidato_destino.orden}` : ''}
              <br />
              {preview.candidato_destino.region}{preview.candidato_destino.provincia ? ` / ${preview.candidato_destino.provincia}` : ''}{preview.candidato_destino.distrito ? ` / ${preview.candidato_destino.distrito}` : ''}
            </div>
          </div>

          {preview.bloqueantes.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded p-3 space-y-1">
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle size={14} /> Bloqueantes detectados
              </div>
              <ul className="list-disc ml-5">
                {preview.bloqueantes.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideBloqueantes}
                  onChange={(e) => setOverrideBloqueantes(e.target.checked)}
                />
                <span className="text-[11px]">Asignar igual (queda con bloqueantes pendientes de subsanar)</span>
              </label>
            </div>
          )}

          <button
            onClick={() => asignarMut.mutate()}
            disabled={asignarMut.isPending || (preview.bloqueantes.length > 0 && !overrideBloqueantes)}
            className="w-full bg-green-600 text-white py-2 rounded text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
          >
            {asignarMut.isPending ? 'Asignando…' : `Asignar DNI ${preview.dni} a esta posición`}
          </button>
        </div>
      )}
    </section>
  );
}
